import { Link } from "react-router-dom";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { InfoTooltip } from "../../../components/general/InfoTooltip.tsx";
import { MoneyLabel } from "../../../components/general/MoneyLabel.tsx";
import { Money, toDecimalFromMoney } from "../../../lib/Money.ts";
import { DEFAULT_CURRENCY } from "../../../types/constants.ts";
import type { CurrencyEnum } from '../../../generated/enums.ts';

export interface OutstandingBillSummaryItem {
    currency: CurrencyEnum | null;
    amount: Money
    count: number;
}

export interface OutstandingBillSummary {
    sales: OutstandingBillSummaryItem[];
    purchases: OutstandingBillSummaryItem[];
    total_sales_bill_count: number;
    total_purchase_bill_count: number;
}

export interface BaseStatsData {
    inflow: { current: number; change: number };
    outflow: { current: number; change: number };
}

export interface StatsProps {
    data?: BaseStatsData;
    outstandingBillSummary?: OutstandingBillSummary | null;
    showOutstandingInvoices: boolean;
}

export function getStatsByCurrency(
    items: OutstandingBillSummaryItem[] | null | undefined,
    currency: CurrencyEnum ,
): { value: Money; count: number; currency: CurrencyEnum } {

    const item = items?.find((a) => a.currency === currency);
    if (!item ) return {
        value: { signed_amount: 0, currency: currency },
        count: 0,
        currency,
    };
    return {
        value: item.amount,
        count: item.count,
        currency
        };
}

export default function Stats({ data, outstandingBillSummary, showOutstandingInvoices }: StatsProps) {
    if (!data) return null;

    const sales = getStatsByCurrency(outstandingBillSummary?.sales, DEFAULT_CURRENCY) ;
    const purchases =  getStatsByCurrency(outstandingBillSummary?.purchases, DEFAULT_CURRENCY) ;

    const stats = [
        {
            id: 2,
            prefix: '€ ',
            goodWhenLow: false,
            name: 'Inkomsten',
            isMoney: true,
            value: data.inflow.current,
            change: data.inflow.change
        },
        {
            id: 1,
            prefix: '€ ',
            goodWhenLow: true,
            name: 'Uitgaven',
            isMoney: true,
            value: data.outflow.current,
            change: data.outflow.change
        },
        
    ]

    return (
        <dl className="mt-2 grid grid-cols-1 gap-4 overflow-hidden md:grid-cols-2 xl:grid-cols-4">
            {stats.map((stat) => (
                <div key={stat.id}
                     className="flex flex-col flex-start text-left p-6 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-all">
                    <dt className="xl:text-lg leading-6 font-medium text-gray-700 2xl:inline-flex items-center justify-between">
                        {stat.name} <br/>
                        <span className='text-sm text-gray-500 font-normal'>
                        {`${new Date().toLocaleString('nl-NL', {month: 'short'})} vs. ${new Date(new Date().setMonth(new Date().getMonth() - 1)).toLocaleString('nl-NL', {month: 'short'})}`}
                        </span>
                    </dt>
                    <dd className="order-first text-xl fond-bold 2xl:text-3xl font-bold tracking-tight text-gray-900 flex justify-between">
                        <span>{stat.prefix} {stat.isMoney &&
                            <MoneyLabel value={stat.value}/>} {!stat.isMoney && stat.value}</span>
                        <Growth value={stat.change} goodWhenLow={stat.goodWhenLow}/>
                    </dd>
                </div>
            ))}

            {showOutstandingInvoices && (
                <>
                    <Link to="/outstanding-sales-invoices" className="flex flex-row items-center justify-between gap-4 p-6 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-all">
                        <div className="flex flex-col flex-start text-left min-w-0">
                            <dd className="text-xl 2xl:text-3xl tracking-tight text-gray-900">
                                <MoneyLabel value={toDecimalFromMoney(sales.value)} showCurrency={true} currency={sales.currency} />
                            </dd>
                            <dt className="text-base font-medium text-gray-700 mt-1 flex items-center gap-1">
                                Open verkoopfacturen ({sales?.count ?? 0})
                                <InfoTooltip position="top" text="Dit betreft alleen de som van facturen in Euro." />
                            </dt>
                        </div>
                        <span className="shrink-0 text-primary bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1">
                            BEKIJK <ArrowRightIcon className="h-4 w-4 shrink-0" />
                        </span>
                    </Link>
                    <Link to="/outstanding-purchase-invoices" className="flex flex-row items-center justify-between gap-4 p-6 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-all">
                        <div className="flex flex-col flex-start text-left min-w-0">
                            <dd className="text-xl 2xl:text-3xl tracking-tight text-gray-900">
                                <MoneyLabel value={toDecimalFromMoney(purchases.value)} showCurrency={true} currency={purchases.currency}/>
                            </dd>
                            <dt className="text-base font-medium text-gray-700 mt-1 flex items-center gap-1">
                                Open inkoopfacturen ({purchases?.count ?? 0})
                                <InfoTooltip position="top" text="Dit betreft alleen de som van facturen in Euro." />
                            </dt>
                        </div>
                        <span className="shrink-0 text-primary bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1">
                            BEKIJK <ArrowRightIcon className="h-4 w-4 shrink-0" />
                        </span>
                    </Link>
                </>
            )}

        </dl>
    )
}
// isHigh badWhenLow
// false true -> green
// false false -> red
// true true -> red
// true false -> green
export function Growth({value, goodWhenLow}) {
    const isHigh = value > -1


    let color = 'text-out-color'
    let bgColor = 'bg-red-50'


    let icon = '↓'

    if (isHigh) {
        icon = '↑'
    }

    if (isHigh != goodWhenLow) {
        color = 'text-in-color'
        bgColor = 'bg-green-50 border'
    }

    return (
        <span
            className={`${color} ${bgColor} border-2 text-xs ml-2 px-2 py-1 rounded-full font-medium inline-flex items-center`}>
            <span className="font-xs mb-1 mr-2">{icon}</span> {value}%
        </span>
    )
}
